import mapboxgl, { GeoJSONSource } from "mapbox-gl";
import { addSourcesIfNotExist } from "../map/sources";
import { loadIcons } from "../map/icons";
import { makeMap } from "../map/map";
import { tripsSources } from "../shift_monitoring/trips/sources";
import { neighborsSources } from "../shift_monitoring/neighbors/sources";
import * as trip from "../shift_monitoring/trips/layers";
import { show as showTrips } from "../shift_monitoring/trips/map";
import * as neighbor from "../shift_monitoring/neighbors/layers";
import { NeighborSourceData } from "../shift_monitoring/neighbors/map";
import { store } from "../../store";
import { Shape, TripGeoDetails, SelectedTrip } from "../../interfaces";
import { GoCancelsRow, NeighborPickupsList, TripsReportsRow } from "../../api";
import { TripDetails } from "../../features/WeeklyReport/DetailScreen/store";
import { convertShiftDateToJpn, convertUTCtoJST } from "../../utils/formatting";
import { convert as neighborsConvert } from "../../services/shift_monitoring/neighbors/map";
import {
    CoordinateObject,
    emptyGeojsonObject,
    point,
} from "../map/convert/geojson";
import * as uuid from "uuid";
import { getSessionCookie } from "../../components/cookies";

const initSources = (map: mapboxgl.Map): void => {
    addSourcesIfNotExist(map, Object.values(tripsSources));
    addSourcesIfNotExist(map, Object.values(neighborsSources));
};

// defaultで他車乗車表示をオフにしていますので、初期化段階でレーヤーを追加しない。
const initLayers = (map: mapboxgl.Map): void => {
    trip.addAllLayersInOrder(map);
};

// Weekly map is composed of same layers/sources as trip, neighbors, but each one is added to its own weekly map
export const initMap = async (
    mapContainerName: string
): Promise<mapboxgl.Map> => {
    const map = makeMap(mapContainerName);
    await map.on("load", async () => {
        initSources(map);
        loadIcons(map);
        initLayers(map);
    });

    map.on("mouseover", () => {
        map.getCanvas().style.cursor = "initial";
    });
    map.on("mouseleave", () => {
        map.getCanvas().style.cursor = "";
    });

    neighbor.setHighlightsWithPopup(map);
    trip.setHighlightsWithPopup(map);
    return map;
};

export const showTrip = async (
    map: mapboxgl.Map,
    selectedTripDetails: TripDetails | null,
    report?: TripsReportsRow[] | null
): Promise<void> => {
    if (!selectedTripDetails || !report || !map) {
        return;
    }

    const tripShown = findCorrectTrip(selectedTripDetails.trip_id, report);

    if (!tripShown) {
        console.log(
            "could not find trip in...",
            report,
            "for id...",
            selectedTripDetails.trip_id
        );
        return;
    }

    const tripGeoDetails: TripGeoDetails = {
        deliverStartLoc: tripShown.deliverStartLoc as Shape,
        unladenStartLoc: tripShown.unladenStartLoc as Shape,
        naviShape: tripShown.naviShape as Shape,
        unladenShape: tripShown.unladenShape as Shape,
    };

    const shiftDate = tripShown.shiftDate
        ? convertShiftDateToJpn(tripShown.shiftDate.toString())
        : "";

    const selectedTrip: SelectedTrip = {
        tripSessionId: tripShown.tripSessionId || null,
        loginSessionId: tripShown.loginSessionId || null,
        shiftDate: shiftDate,
        driverCode: tripShown.driverCode || null,
        driverName: tripShown.driverName || null,
        unladenStartTs: convertUTCtoJST(tripShown.unladenStartTs),
        unladenStartTsRaw: tripShown.unladenStartTs || null,
        boardingTs: convertUTCtoJST(tripShown.boardingTs) || null,
        boardingType: tripShown.boardingType || null,
        details: tripGeoDetails,
    };

    const keepCenter = true;
    showTrips(map, selectedTrip, keepCenter);
};

export const refreshMapBounds = (
    map: mapboxgl.Map,
    selectedTripDetails: TripDetails | null,
    report?: TripsReportsRow[] | null
) => {
    if (!selectedTripDetails || !report || !map) {
        return;
    }

    const tripShown = findCorrectTrip(selectedTripDetails.trip_id, report);

    if (!tripShown) {
        console.log(
            "could not find trip in...",
            report,
            "for id...",
            selectedTripDetails.trip_id
        );
        return;
    }

    const tripGeoDetails: TripGeoDetails = {
        deliverStartLoc: tripShown.deliverStartLoc as Shape,
        unladenStartLoc: tripShown.unladenStartLoc as Shape,
        naviShape: tripShown.naviShape as Shape,
        unladenShape: tripShown.unladenShape as Shape,
    };

    const shiftDate = tripShown.shiftDate
        ? convertShiftDateToJpn(tripShown.shiftDate.toString())
        : "";

    const selectedTrip: SelectedTrip = {
        tripSessionId: tripShown.tripSessionId || null,
        loginSessionId: tripShown.loginSessionId || null,
        shiftDate: shiftDate,
        driverCode: tripShown.driverCode || null,
        driverName: tripShown.driverName || null,
        unladenStartTs: convertUTCtoJST(tripShown.unladenStartTs),
        unladenStartTsRaw: tripShown.unladenStartTs || null,
        boardingTs: convertUTCtoJST(tripShown.boardingTs) || null,
        boardingType: tripShown.boardingType || null,
        details: tripGeoDetails,
    };
    const bounds = calculateNewBounds(selectedTrip);

    if (
        selectedTrip &&
        selectedTrip.details &&
        selectedTrip.details.deliverStartLoc
    ) {
        const first: any = selectedTrip.details.deliverStartLoc.shape;
        map.setCenter(first);
    }

    if (bounds) {
        map.fitBounds(bounds, { linear: true });
        const nb = map.getBounds();
    }
    return bounds;
};

const calculateNewBounds = (
    trip: SelectedTrip
): mapboxgl.LngLatBounds | null => {
    const allLats: number[] = [];
    const allLngs: number[] = [];

    if (!trip.details) {
        return null;
    }
    const geoDetails = trip.details;

    if (geoDetails.deliverStartLoc) {
        const shape = geoDetails.deliverStartLoc.shape;
        allLats.push(shape[1]);
        allLngs.push(shape[0]);
    }
    if (geoDetails.unladenStartLoc) {
        const shape = geoDetails.unladenStartLoc.shape;
        allLats.push(shape[1]);
        allLngs.push(shape[0]);
    }
    if (geoDetails.unladenShape) {
        const shape = geoDetails.unladenShape.shape;
        shape.forEach((el: number[]) => {
            console.log("Next GEO SHAPE:", el, el[0]);
            if (typeof el[0] == typeof 3) {
                allLats.push(el[1]);
                allLngs.push(el[0]);
            } else {
                for (let i = 0; i < el.length; i++) {
                    const nextCoords = el[i];
                    const lng = nextCoords[0];
                    const lat = nextCoords[1];
                    allLats.push(lat);
                    allLngs.push(lng);
                }
                const pt1 = el[0];
                const pt2 = el[1];
                allLats.push(pt1[1]);
                allLngs.push(pt1[0]);
                allLats.push(pt2[1]);
                allLngs.push(pt2[0]);
            }
        });
    }
    if (geoDetails.naviShape) {
        const shape = geoDetails.naviShape.shape;
        shape.forEach((el: number[]) => {
            if (typeof el[0] == typeof 3) {
                allLats.push(el[1]);
                allLngs.push(el[0]);
            } else {
                const pt1 = el[0];
                const pt2 = el[1];
                allLats.push(pt1[1]);
                allLngs.push(pt1[0]);
                allLats.push(pt2[1]);
                allLngs.push(pt2[0]);
            }
        });
    }
    const minLat = Math.min(...allLats);
    const maxLat = Math.max(...allLats);
    const minLng = Math.min(...allLngs);
    const maxLng = Math.max(...allLngs);
    const maxBounds = new mapboxgl.LngLatBounds(
        [minLng - 0.03, minLat - 0.03],
        [maxLng + 0.03, maxLat + 0.03]
    );
    return maxBounds;
};

export const findCorrectTrip = (
    tripId: string | null,
    tripsList: TripsReportsRow[]
): TripsReportsRow | null => {
    if (!tripId || !tripsList) {
        return null;
    }
    let trip: any = null;
    for (let i = 0; i < tripsList.length; i++) {
        const rec = tripsList[i];
        if (rec.tripSessionId == tripId) {
            trip = rec;
        }
    }
    return trip;
};

export const showGoCancels = async (
    map: mapboxgl.Map,
    mapId?: string | null
) => {
    const state = store.getState();
    const detailState = state.detailScreen;
    const go_cancels = detailState.goCancels;
    console.debug(
        "Weekly go_cancel data:",
        go_cancels,
        mapId,
        detailState.expandedContainerName,
        detailState.selectedShiftId
    );
    if (mapId) {
        const passedMapName = mapId.split("_map")[0];
        if (
            passedMapName !== detailState.selectedShiftId ||
            passedMapName !== go_cancels.shiftId
        ) {
            return;
        }
    }
    if (!go_cancels || !go_cancels.data) {
        return;
    }
    if (!map) {
        return;
    }

    let goCancelsJson: any = null;

    const _goCancelsJson = go_cancels.data.map((x: GoCancelsRow) => {
        const loc = x.goCancelLocCoords as any;
        return {
            ...x,
            coordinates: loc.shape,
            id: uuid.v4(),
        };
    });
    goCancelsJson = point(_goCancelsJson as CoordinateObject[]);

    try {
        console.log("Map loaded, set goCancelsJson.");
        (map.getSource(tripsSources.goCancelLocSrc) as GeoJSONSource).setData(
            goCancelsJson
        );
        trip.addGoCancelsLayers(map);
    } catch (err) {
        console.log("Map must load first, set goCancelsJson.");
        await map.on("load", async () => {
            (map.getSource(
                tripsSources.goCancelLocSrc
            ) as GeoJSONSource).setData(goCancelsJson);
            trip.addGoCancelsLayers(map);
        });
    }
};

export const showNeighbor = async (
    map: mapboxgl.Map,
    show: boolean,
    shift?: string | null
): Promise<void> => {
    const state = store.getState();
    const detailState = state.detailScreen;
    const data = detailState.neighborsPerTrip;

    console.debug("Showing Neighbors for shift and data:", shift, data);

    if (!map) {
        return;
    }

    if (shift !== data.shift) {
        neighbor.removeLayers(map);
        return;
    }

    readyNeighborData(map, data.neighbors);
    try {
        if (show) {
            neighbor.addAllLayersInOrder(map);
        } else {
            neighbor.removeLayers(map);
        }
    } catch (err) {
        console.log(
            "Neighbors map: Error adding layers to map. Could be not yet loaded.",
            err
        );
        await map.on("load", async () => {
            if (show) {
                neighbor.addAllLayersInOrder(map);
            } else {
                neighbor.removeLayers(map);
            }
        });
    }
};

// TODO refactor: Should Trips also use its own Show method vs the one from shift_monitoring?
// move neighbors convert/show from shift_monitoring to some more common place?
export const readyNeighborData = async (
    map: mapboxgl.Map,
    report?: NeighborPickupsList | null
): Promise<void> => {
    const state = store.getState();
    const detailState = state.detailScreen;
    const commonState = state.common;
    if (!getSessionCookie().consoleUserPermissions.includes("neighbors")) {
        return;
    }

    let neighbors: any = detailState.neighborsPerTrip;

    if (report) {
        const trip = detailState.selectedTripDetails;
        const tripId = trip ? trip.trip_id : "";
        const _r = report[tripId];
        neighbors = _r;
    }

    if (!neighbors) {
        console.debug("Neighbors not shown: No report was selected");
        return;
    }
    const data = neighborsConvert(neighbors);

    try {
        addNeighborSourceData(map, data);
    } catch (err) {
        console.log(
            "Neighbors map: Error adding sourceData to map. Could be map is not yet loaded.",
            err
        );
        await map.on("load", async () => {
            addNeighborSourceData(map, data);
        });
    }
};

export const resetNeighborSourceData = (map: mapboxgl.Map) => {
    if (!map) {
        return;
    }

    const geo = emptyGeojsonObject();

    if (!map.getSource(neighborsSources.neighborsCruiseNGSrc)) {
        return;
    }
    (map.getSource(
        neighborsSources.neighborsCruiseNGSrc
    ) as GeoJSONSource).setData(geo);

    (map.getSource(
        neighborsSources.neighborsCruiseOKSrc
    ) as GeoJSONSource).setData(geo);

    (map.getSource(neighborsSources.neighborsGoNGSrc) as GeoJSONSource).setData(
        geo
    );

    (map.getSource(neighborsSources.neighborsGoOKSrc) as GeoJSONSource).setData(
        geo
    );
};

const addNeighborSourceData = (
    map: mapboxgl.Map,
    neighborsData: NeighborSourceData
): void => {
    const { goNG, goOK, cruiseOK, cruiseNG, nOK } = neighborsData;

    (map.getSource(
        neighborsSources.neighborsCruiseNGSrc
    ) as GeoJSONSource).setData(cruiseNG);

    (map.getSource(
        neighborsSources.neighborsCruiseOKSrc
    ) as GeoJSONSource).setData(cruiseOK);

    (map.getSource(neighborsSources.neighborsGoNGSrc) as GeoJSONSource).setData(
        goNG
    );

    (map.getSource(neighborsSources.neighborsGoOKSrc) as GeoJSONSource).setData(
        goOK
    );
};
